/* eslint-disable max-len */
import { AvatarCustom, CentralLoadingIndicator, CustomStepper } from "../../../components";
import { Button, CircularProgress, useMediaQuery } from "@mui/material";
import { ASSIGNED_BOOKING_TYPE, MODALS, STEPPER_STEPS } from "../../../config/params";
import React, { useEffect, useState } from "react";
import { capitalizeFirstLetterGeneric, formatDoctorNames, getEventDataForCustomerRating } from "../../../utils/genericUtils";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CloseIcon } from "../../../components/Icons";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import RecommendationCard from "./RecommendationCard";
import StarRatingCard from "./StarRatingCard";
import VisitFeedback1 from "./Feedback1";
import VisitFeedback2 from "./Feedback2";
import { fromUnixToShortWeekFormat } from "../../../utils/unix-timestamp";
import { toastMessageActions } from "../../../state/request";
import { useAppointmentRatingQuestions } from "../../../services/appointment-rating/appointment-rating.service";
import { useAppointmentRatingStore } from "../../../hooks";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useDispatch } from "react-redux";
import { useModal } from "../../../context/ModalContext";
import { useUpdateAppointmentRating } from "../../../services/appointment-rating/appointment-rating-mutation.service";
import { userActivityLogger } from "../../../utils/mixpanel";
import { CUSTOMER_REFERRAL_SOURCES } from "../../../navigation/params";

const RatingReviewModal = ()=>{
  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const { userRating, setAppointmentStarRating, setAppointmentRatingNull, setAppointmentStepOneFeedback } =
   useAppointmentRatingStore();
  const { appointmentDetails, setAppointmentDetails } = useAppointmentStore();

  const { openModal, closeModal } = useModal();
  const dispatch = useDispatch();

  const { getAppointmentRatingQuestion, questionsList, loading } = useAppointmentRatingQuestions();
  const { updateAppointmentRatingMutation, loading: updateLoading, data } = useUpdateAppointmentRating();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isUpdateCalled, setIsUpdateCalled] = useState<boolean>(false);
  const [fieldError, setFieldError] = useState<any>(false);
  const { setAppointmentId, appointmentPlaceId }=useModal();

  const [userRatingValue, setUserRatingValue] = useState<any | null>({
    starRating: userRating?.starRating ? userRating.starRating : 0,
    recommendedRating: 10,
    feedback1: "",
    feedback2: "",
  });

  useEffect(()=>{
    getAppointmentRatingQuestion();
    setAppointmentId("");
  }, []);

  const { petName, day, month, resources, time } = getEventDataForCustomerRating(appointmentDetails && appointmentDetails);

  const appointmentInfo = {
    pet_name: petName || "",
    doctor_name: formatDoctorNames(resources) || "",
    date: `${month} ${day}` || "",
    time_slot: time || "",
  };

  const currentStepMapping: {[key : number]: any} = {
    0: { event_name: "STAR_RATING_SUBMITTED", event_current_step_value: "star rating" },
    1: { event_name: "NPS_RATING_SUBMITTED", event_current_step_value: "nps rating" },
    2: { event_name: "QUESTION1_RESPONSE_SUBMITTED", event_current_step_value: "question 1" },
    3: { event_name: "QUESTION2_RESPONSE_SUBMITTED", event_current_step_value: "question 2" },
  };

  const answersMapping: { [key: number]: any } = {
    0: {
      questionId: questionsList.filter((q) => q.index === 1)?.[0]?._id,
      answer: userRating.starRating?.toString(),
    },
    1: {
      questionId: questionsList.filter((q) => q.index === 2)?.[0]?._id,
      answer: userRatingValue.recommendedRating?.toString(),
    },
    2: {
      questionId: questionsList.filter((q) => q.index === 3)?.[0]?._id,
      answer: userRatingValue?.feedback1,
    },
    3: {
      questionId: questionsList.filter((q) => q.index === 4)?.[0]?._id,
      answer: userRatingValue?.feedback2,
    },
  };


  const handleRatingChange = (value: any = "") => {
    const updateFunctions = {
      0: () => {
        setAppointmentStarRating(value);
        setUserRatingValue({ ...userRatingValue, starRating: value });

        userActivityLogger(MIXPANEL_EVENTS.STAR_RATING_SELECTED,
          {
            rating_value: value,
            current_step: "star rating",
            ...appointmentInfo,
          });
      },
      1: () => {
        setUserRatingValue({ ...userRatingValue, recommendedRating: value });
        userActivityLogger(MIXPANEL_EVENTS.NPS_RATING_SELECTED,
          {
            rating_value: value,
            current_step: "nps rating",
            ...appointmentInfo,
          });
      },
      2: () => {
        setUserRatingValue({ ...userRatingValue, feedback1: value });
      },
      3: () => {
        setUserRatingValue({ ...userRatingValue, feedback2: value });
      },
    };

    updateFunctions[activeStep as keyof typeof updateFunctions]?.();
  };


  const getStepDetail = (step: number) => {
    const question = questionsList.filter((q) => q.index === step + 1)?.[0];

    switch (step) {
    case 0:
      return (
        <StarRatingCard
          value={userRating.starRating}
          handleRatingChange={handleRatingChange}
          question={question}
        />
      );
    case 1:
      return (
        <RecommendationCard
          value={userRatingValue.recommendedRating}
          handleRatingChange={handleRatingChange}
          question={question}
        />
      );
    case 2:
      return (
        <VisitFeedback1
          value={userRatingValue.feedback1}
          handleRatingChange={handleRatingChange}
          question={question}
        />
      );
    case 3:
      return (
        <VisitFeedback2
          value={userRatingValue.feedback2}
          handleRatingChange={handleRatingChange}
          question={question}
        />
      );
    }
  };


  useEffect(()=>{
    // error mapping for each step in the rating process to disable the continue button
    const errorMapping = {
      0: userRating.starRating < 1,
      1: userRatingValue.recommendedRating < 1,
      2: userRatingValue.feedback1 === "",
      3: userRatingValue.feedback2 === "",
    };

    setFieldError(errorMapping[activeStep as keyof typeof errorMapping]);
  }, [activeStep, userRatingValue]);

  const handleContinue = () => {
    if (appointmentDetails) {
      setIsUpdateCalled(true);

      const appointmentId = appointmentDetails.id;

      if (activeStep in answersMapping) {
        // calling the mutation api to update the rating
        updateAppointmentRatingMutation({ appointmentId, answers:
         [answersMapping[activeStep as keyof typeof answersMapping]] });

        // event for mixpanel below
        userActivityLogger(MIXPANEL_EVENTS[currentStepMapping[activeStep as keyof typeof answersMapping].event_name as keyof typeof MIXPANEL_EVENTS],
          {
            rating_value: answersMapping[activeStep as keyof typeof answersMapping]?.answer,
            current_step: currentStepMapping[activeStep as keyof typeof answersMapping].event_current_step_value,
            ...appointmentInfo,
          });
      } else {
        setIsUpdateCalled(false);
      }
    }
  };

  const handleBackStep = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };


  const handleCloseModal = () => {
    userActivityLogger(MIXPANEL_EVENTS.REVIEW_PAGE_CLOSED,
      {
        rating_value: answersMapping[activeStep as keyof typeof answersMapping]?.answer,
        current_step: currentStepMapping[activeStep as keyof typeof answersMapping]?.event_current_step_value,
        ...appointmentInfo,
      });
    if (userRatingValue.starRating >= 4 && appointmentPlaceId && (appointmentPlaceId !== "")) {
      setAppointmentStepOneFeedback(userRatingValue?.feedback1);
      openModal(MODALS.POST_TO_GOOGLE_MODAL);
    } else {
      setAppointmentRatingNull();
      setUserRatingValue(null);
      setAppointmentDetails(null);
      if (activeStep === 0) {
        closeModal();
      } else {
        openModal(MODALS.QUICK_SHARE_UPSELL_MODAL, CUSTOMER_REFERRAL_SOURCES.AFTER_REVIEW);
      }
    }
  };

  useEffect(()=>{
    if (data?.success && isUpdateCalled) {
      setAppointmentId(data.appointmentPlaceId);
      setActiveStep(activeStep + 1);
      setIsUpdateCalled(false);
    }
    if (activeStep === 3 && data?.success) {
      setAppointmentStepOneFeedback(userRatingValue?.feedback1);
      setAppointmentId(data.appointmentPlaceId);
      if (userRatingValue.starRating >= 4 && data.appointmentPlaceId && data.appointmentPlaceId!=="") {
        userActivityLogger(MIXPANEL_EVENTS.GOOGLE_REVIEW_PROMPT_TRIGGERED,
          {
            rating_value: userRatingValue?.starRating,
            current_step: "google review",
            ...appointmentInfo,
          });
        openModal(MODALS.POST_TO_GOOGLE_MODAL);
      } else {
        dispatch(toastMessageActions.setGlobalAlert({ severity: "success",
          message: "Thank you for your review!" }));
        handleCloseModal();
      }
    }
  }, [data]);

  const bookingDate = appointmentDetails &&
  fromUnixToShortWeekFormat(appointmentDetails?.start_at, appointmentDetails?.location.timeZone);


  if (loading) {
    return <CentralLoadingIndicator/>;
  }

  return (
    <div className="w-full px-1 sm:px-4 py-1 !h-full flex flex-col justify-between overflow-y-auto">
      <div className="flex flex-col gap-3">
        <div className={`mb-6 flex w-full ${activeStep !==0 ? "justify-between" : "justify-end"}`}>
          {activeStep!==0 && <ArrowBackIcon
            onClick={handleBackStep}
            className="text-light-black cursor-pointer mt-1"
          />}
          <CloseIcon
            onClick={handleCloseModal}
            className="text-black cursor-pointer mt-1"
          />
        </div>
        <div className="flex flex-col gap-10 items-center self-stretch flex-1">
          {/* details */}
          <div id="appointment-details-section" className="flex items-start gap-3 self-stretch">
            <div id="details" className="flex flex-col items-start gap-[10px] flex-1">
              <h2 className="text-lg sm:text-2xl font-bold leading-6 text-wrap max-w-[90%]">
                    How was {capitalizeFirstLetterGeneric(appointmentDetails?.pet.name)}&apos;s Appointment?
              </h2>
              <div className="text-sm sm:text-base leading-5">
                <p>GoodVets {appointmentDetails?.location.locationName}<span className="mx-1">&#xb7;</span> {bookingDate?.weekday}, {" "}
                  {bookingDate?.month} {bookingDate?.day}</p>
                <p>at {bookingDate?.time} {
                  appointmentDetails && (appointmentDetails?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.NORMAL_BOOKING) &&
                  appointmentDetails?.resources?.length &&
                  appointmentDetails.resources[0].name &&
                  (formatDoctorNames(appointmentDetails?.resources) !== "") &&
                  <span>with <b>{formatDoctorNames(appointmentDetails?.resources)}</b></span>}</p>
              </div>
            </div>
            {appointmentDetails && <div id="avatars">
              <AvatarCustom
                name={appointmentDetails?.pet?.name}
                width={isSmallScreen ? 40: 56}
                height={isSmallScreen ? 40: 56}
                image={appointmentDetails?.pet?.image!}
                className="border-2 border-white bg-white rounded-full"
              />
            </div>}
          </div>
          {/* details end */}

          <CustomStepper
            steps={STEPPER_STEPS.APPOINMENT_RATING}
            activeStep={activeStep}
            hideLabel={true}
          />

          {getStepDetail(activeStep)}

        </div>
      </div>
      <div>
        <Button className="!font-bold" variant="contained" color="primary" fullWidth onClick={handleContinue}
          disabled={fieldError || updateLoading}>
          {updateLoading ? <CircularProgress color="inherit" size={20} /> :
            activeStep === 3 ? "Submit" : "Continue"
          }
        </Button>
      </div>
    </div>
  );
};

export default RatingReviewModal;
